<template>
  <div class="achievements-page">
    <!-- 顶部标题 -->
    <div class="ach-head">
      <div class="ach-head-text">
        <h2 class="ach-title">我的成就</h2>
        <p class="ach-subtitle">记录每一个值得纪念的时刻</p>
      </div>
      <div class="ach-head-count" v-if="overview">
        已获得 <strong>{{ overview.unlockedCount }}</strong> / {{ overview.totalCount }}
      </div>
    </div>

    <!-- 概览 -->
    <div v-if="overview" class="ach-overview">
      <div class="ov-card">
        <div class="ov-value">{{ overview.unlockedCount }}<span class="ov-unit">/ {{ overview.totalCount }}</span></div>
        <div class="ov-label">已获得成就</div>
      </div>
      <div class="ov-card">
        <div class="ov-value">{{ overview.completionPercent }}<span class="ov-unit">%</span></div>
        <div class="ov-label">成就完成度</div>
      </div>
      <div class="ov-card">
        <div class="ov-value ov-latest">
          <template v-if="overview.latestName">{{ overview.latestName }}</template>
          <template v-else>—</template>
        </div>
        <div class="ov-label">最近获得</div>
      </div>
      <div class="ov-card">
        <div class="ov-value">{{ overview.currentStreakDays }}<span class="ov-unit">天</span></div>
        <div class="ov-label">当前连续记录</div>
      </div>
    </div>

    <!-- 分类导航 -->
    <div class="ach-cats">
      <button
        v-for="cat in categories"
        :key="cat.key"
        type="button"
        class="ach-cat"
        :class="{ 'is-active': activeCat === cat.key }"
        @click="activeCat = cat.key"
      >
        {{ cat.label }}
        <span class="cat-count">{{ catCount(cat.key) }}</span>
      </button>
    </div>

    <!-- 加载 / 错误 -->
    <div v-if="loading" class="ach-hint">正在加载成就…</div>
    <div v-else-if="error" class="ach-hint is-error">{{ error }}</div>

    <!-- 徽章墙 -->
    <div v-else class="ach-wall">
      <div
        v-for="item in filtered"
        :key="item.code"
        class="ach-card"
        :class="{
          'is-locked': !item.unlocked,
          'is-hidden-locked': item.hidden && !item.unlocked,
          'is-focus': focusCode === item.code
        }"
        @click="openDetail(item)"
      >
        <AchievementBadge
          class="card-badge"
          :level="item.level"
          :icon="item.icon"
          :locked="!item.unlocked"
          :size="84"
        />

        <div class="card-name">{{ item.name }}</div>
        <div class="card-desc">{{ item.description }}</div>

        <!-- 已解锁：解锁时间 -->
        <div v-if="item.unlocked" class="card-foot is-unlocked">
          <span class="dot" :style="{ background: item.levelColor }"></span>
          {{ formatDate(item.unlockedAt) }}获得
        </div>

        <!-- 未解锁 + 有进度：进度条 -->
        <div v-else-if="item.progressText" class="card-foot is-progress">
          <div class="bar">
            <span class="bar-fill" :style="{ width: item.progressPercent + '%', background: item.levelColor }"></span>
          </div>
          <div class="bar-text">{{ item.progressText }}</div>
        </div>

        <!-- 未解锁 + 无进度：解锁条件 -->
        <div v-else class="card-foot is-condition">{{ item.conditionText }}</div>

        <div class="card-level" :style="{ color: item.levelColor, borderColor: item.levelColor }">
          {{ item.levelLabel }}
        </div>
      </div>

      <div v-if="!filtered.length" class="ach-hint">该分类下暂无成就</div>
    </div>

    <!-- 成就详情 -->
    <transition name="ach-detail">
      <div v-if="detail" class="detail-overlay" @click.self="detail = null">
        <div class="detail-card">
          <button type="button" class="detail-close" title="关闭" @click="detail = null">×</button>

          <AchievementBadge
            class="detail-badge"
            :level="detail.level"
            :icon="detail.icon"
            :locked="!detail.unlocked"
            :size="112"
          />

          <div class="detail-name">{{ detail.name }}</div>
          <div class="detail-tags">
            <span class="tag" :style="{ color: detail.levelColor, borderColor: detail.levelColor }">
              {{ detail.levelLabel }}
            </span>
            <span class="tag is-cat">{{ detail.categoryLabel }}</span>
            <span v-if="detail.hidden" class="tag is-hidden">隐藏成就</span>
          </div>

          <p class="detail-desc">{{ detail.description }}</p>

          <div v-if="detail.peerName" class="detail-peer">关联好友：{{ detail.peerName }}</div>

          <div class="detail-row">
            <span class="detail-label">解锁条件</span>
            <span class="detail-value">{{ detail.conditionText }}</span>
          </div>

          <div v-if="detail.progressText" class="detail-row">
            <span class="detail-label">当前进度</span>
            <span class="detail-value">{{ detail.progressText }}</span>
          </div>
          <div v-if="detail.progressText && !detail.unlocked" class="detail-bar">
            <span class="bar-fill" :style="{ width: detail.progressPercent + '%', background: detail.levelColor }"></span>
          </div>

          <div class="detail-row">
            <span class="detail-label">解锁状态</span>
            <span class="detail-value" :class="{ 'is-unlocked': detail.unlocked }">
              {{ detail.unlocked ? '已解锁' : '尚未解锁' }}
            </span>
          </div>

          <div v-if="detail.unlocked" class="detail-row">
            <span class="detail-label">解锁时间</span>
            <span class="detail-value">{{ formatDate(detail.unlockedAt) }}</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import AchievementBadge from './AchievementBadge.vue'
import { getAchievementWall } from '@/api/achievement.js'
import { enqueueUnlocks } from './achievementStore.js'

/**
 * 分类导航的固定项。
 *
 * 「全部」与「个人足迹」是**前端派生视图**，不是后端分类：
 * 前者不筛选，后者是 personal=true 的分类并集。
 * 真正的分类（学习 / 睡眠 / …）由后端随徽章墙一起下发，
 * 这里不再维护一份分类码常量 —— 后台新增分类时前端无需发版。
 */
const FIXED_CATEGORIES = [
  { key: 'ALL', label: '全部' },
  { key: 'PERSONAL', label: '个人足迹' }
]

export default {
  name: 'AchievementsPage',
  components: { AchievementBadge },
  props: {
    /** 需要定位 / 高亮的成就编码（从解锁浮窗「查看成就」跳转而来）。 */
    focusCode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      categories: FIXED_CATEGORIES,
      /** 「个人足迹」包含的分类码，由后端下发。 */
      personalCategories: [],
      loading: true,
      error: '',
      overview: null,
      list: [],
      activeCat: 'ALL',
      detail: null
    }
  },
  computed: {
    filtered() {
      if (this.activeCat === 'ALL') return this.list
      if (this.activeCat === 'PERSONAL') {
        return this.list.filter(a => this.personalCategories.includes(a.category))
      }
      return this.list.filter(a => a.category === this.activeCat)
    }
  },
  created() {
    this.load()
  },
  watch: {
    // 页面已打开时又从浮窗点了「查看成就」：直接定位到新成就
    focusCode(val) {
      if (val) this.applyFocus()
    }
  },
  methods: {
    async load() {
      this.loading = true
      this.error = ''
      try {
        const res = await getAchievementWall()
        if (res && res.success && res.data) {
          this.overview = res.data.overview || null
          this.list = Array.isArray(res.data.list) ? res.data.list : []
          this.applyCategories(res.data.categories)
          // 打开成就页时后端可能刚解锁了新成就，同样弹提醒
          enqueueUnlocks(res.data.newlyUnlocked)
          this.applyFocus()
        } else {
          this.error = (res && res.message) || '成就加载失败'
        }
      } catch (e) {
        this.error = '网络异常，成就加载失败'
      } finally {
        this.loading = false
      }
    },
    /**
     * 用后端下发的分类重建导航。
     *
     * 后端未下发时（老版本接口）保留固定项，不至于把导航清空。
     */
    applyCategories(list) {
      if (!Array.isArray(list) || !list.length) return
      this.personalCategories = list.filter(c => c.personal).map(c => c.code)
      this.categories = FIXED_CATEGORIES.concat(
        list.map(c => ({ key: c.code, label: c.label }))
      )
      if (this.activeCat !== 'ALL' && this.activeCat !== 'PERSONAL'
        && !list.some(c => c.code === this.activeCat)) {
        this.activeCat = 'ALL'
      }
    },
    /** 定位到「刚刚解锁」的成就：直接展开详情。 */
    applyFocus() {
      if (!this.focusCode) return
      const item = this.list.find(a => a.code === this.focusCode)
      if (item) {
        this.activeCat = 'ALL'
        this.detail = item
      }
    },
    /** 分类下的成就数（已解锁 / 总数）。 */
    catCount(key) {
      let items = this.list
      if (key === 'PERSONAL') {
        items = items.filter(a => this.personalCategories.includes(a.category))
      } else if (key !== 'ALL') {
        items = items.filter(a => a.category === key)
      }
      const unlocked = items.filter(a => a.unlocked).length
      return `${unlocked}/${items.length}`
    },
    openDetail(item) {
      this.detail = item
    },
    /** 2026年10月6日 */
    formatDate(value) {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
    }
  }
}
</script>

<style scoped>
.achievements-page {
  /* 上内边距由外层 .achievements-section 的 20px 提供（与个人中心其它子页面的
     .section-head 一致），这里不再额外加 6px，保证四个子页面标题在同一水平线上。 */
  padding: 0 0 24px;
  font-family: 'DynaPuff', 'OPPOSans', 'Microsoft YaHei', sans-serif;
  color: var(--lp-text, #26332b);
}

/* 顶部 */
.ach-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.ach-title {
  font-size: 24px;
  font-weight: 800;
  margin: 0;
}

.ach-subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--lp-text-2, #5f7268);
}

.ach-head-count {
  font-size: 14px;
  color: var(--lp-text-2, #5f7268);
}

.ach-head-count strong {
  font-size: 20px;
  color: var(--lp-primary-dark, #2f7a4a);
}

/* 概览 */
.ach-overview {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.ov-card {
  background: var(--lp-surface-2, #f7fbf8);
  border: 1px solid var(--lp-border, #e3efe6);
  border-radius: 0.75rem;
  padding: 14px 16px;
}

.ov-value {
  font-size: 22px;
  font-weight: 800;
  color: var(--lp-primary-dark, #2f7a4a);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ov-latest {
  font-size: 18px;
  color: var(--lp-text, #26332b);
}

.ov-unit {
  font-size: 13px;
  font-weight: 600;
  color: var(--lp-text-3, #94a59b);
  margin-left: 2px;
}

.ov-label {
  margin-top: 6px;
  font-size: 12px;
  color: var(--lp-text-2, #5f7268);
}

/* 分类 */
.ach-cats {
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ach-cat {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  border: 2px solid var(--lp-border, #e3efe6);
  background: var(--lp-surface, #fff);
  color: var(--lp-text-2, #5f7268);
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ach-cat:hover {
  border-color: var(--lp-primary-soft2, #d5ebdb);
  background: var(--lp-primary-soft, #e9f5ec);
}

.ach-cat.is-active {
  background: var(--lp-primary, #4aa46a);
  border-color: var(--lp-primary, #4aa46a);
  color: #fff;
}

.cat-count {
  font-size: 11px;
  font-weight: 700;
  opacity: 0.75;
}

/* 徽章墙 */
.ach-wall {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(178px, 1fr));
  gap: 14px;
}

.ach-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 18px 14px 16px;
  background: var(--lp-surface, #fff);
  border: 1px solid var(--lp-border, #e3efe6);
  border-radius: 1rem;
  box-shadow: var(--lp-shadow, 0 2px 10px rgba(52, 102, 68, 0.06));
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.ach-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(52, 102, 68, 0.12);
  border-color: var(--lp-primary-soft2, #d5ebdb);
}

.ach-card.is-locked {
  background: var(--lp-surface-2, #f7fbf8);
}

.ach-card.is-focus {
  border-color: var(--lp-primary, #4aa46a);
  box-shadow: 0 0 0 3px var(--lp-primary-soft, #e9f5ec);
}

.card-badge {
  margin-bottom: 10px;
}

.card-name {
  font-size: 15px;
  font-weight: 700;
  color: var(--lp-text, #26332b);
  line-height: 1.3;
}

.ach-card.is-locked .card-name {
  color: var(--lp-text-2, #5f7268);
}

.card-desc {
  margin-top: 5px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--lp-text-3, #94a59b);
  min-height: 36px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-foot {
  margin-top: 10px;
  width: 100%;
  font-size: 12px;
  min-height: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.card-foot.is-unlocked {
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--lp-primary-dark, #2f7a4a);
  font-weight: 600;
}

.card-foot.is-unlocked .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
}

.card-foot.is-progress .bar {
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: var(--lp-border-soft, #eef5f0);
  overflow: hidden;
}

.bar-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  transition: width 0.4s ease;
}

.card-foot.is-progress .bar-text {
  margin-top: 6px;
  font-size: 12px;
  color: var(--lp-text-2, #5f7268);
  font-weight: 600;
}

.card-foot.is-condition {
  color: var(--lp-text-3, #94a59b);
  line-height: 1.45;
}

.card-level {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid;
  border-radius: 999px;
  background: var(--lp-surface, #fff);
}

/* 提示 */
.ach-hint {
  margin-top: 26px;
  text-align: center;
  font-size: 14px;
  color: var(--lp-text-3, #94a59b);
}

.ach-hint.is-error {
  color: #d9534f;
}

/* 详情 */
.detail-overlay {
  position: fixed;
  inset: 0;
  z-index: 2500;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(38, 51, 43, 0.35);
  padding: 20px;
}

.detail-card {
  position: relative;
  width: 380px;
  max-width: 92vw;
  max-height: 88vh;
  overflow-y: auto;
  padding: 28px 26px 22px;
  background: var(--lp-surface, #fff);
  border: 1px solid var(--lp-border, #e3efe6);
  border-radius: 1.25rem;
  box-shadow: 0 18px 46px rgba(52, 102, 68, 0.18);
  text-align: center;
}

.detail-close {
  position: absolute;
  top: 10px;
  right: 12px;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: var(--lp-text-3, #94a59b);
  cursor: pointer;
  border-radius: 50%;
}

.detail-close:hover {
  color: var(--lp-text, #26332b);
  background: var(--lp-surface-2, #f7fbf8);
}

.detail-badge {
  margin-bottom: 12px;
}

.detail-name {
  font-size: 21px;
  font-weight: 800;
}

.detail-tags {
  margin-top: 8px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
}

.tag {
  padding: 1px 9px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid;
  border-radius: 999px;
}

.tag.is-cat {
  color: var(--lp-text-2, #5f7268);
  border-color: var(--lp-border, #e3efe6);
}

.tag.is-hidden {
  color: #8264c4;
  border-color: #8264c4;
}

.detail-desc {
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--lp-text-2, #5f7268);
}

.detail-peer {
  margin-top: 8px;
  font-size: 13px;
  color: var(--lp-text-3, #94a59b);
}

.detail-row {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  text-align: left;
}

.detail-label {
  color: var(--lp-text-3, #94a59b);
  flex: none;
}

.detail-value {
  color: var(--lp-text, #26332b);
  text-align: right;
}

.detail-value.is-unlocked {
  color: var(--lp-primary-dark, #2f7a4a);
  font-weight: 700;
}

.detail-bar {
  margin-top: 8px;
  height: 7px;
  border-radius: 999px;
  background: var(--lp-border-soft, #eef5f0);
  overflow: hidden;
}

.ach-detail-enter-active,
.ach-detail-leave-active {
  transition: opacity 0.22s ease;
}

.ach-detail-enter,
.ach-detail-leave-to {
  opacity: 0;
}

@media (max-width: 760px) {
  .ach-overview {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ach-wall {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
}
</style>
